import { X } from "lucide-react";
import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { WaitlistForm } from "@/components/marketing/WaitlistForm";

interface WaitlistModalProps {
  open: boolean;
  onClose: () => void;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

// The waitlist capture that opens when a visitor submits the hero composer.
// During the waitlist period there is no chat backend to answer them, so the
// composer funnels straight here. Rendered through a portal (same reason as
// MobileMenu — the hero's glass chrome would otherwise clip a `fixed` child)
// with a real dialog contract: labelled, focus-restoring, and dismissible by
// Escape, the close button, or a click on the backdrop.
export function WaitlistModal({ open, onClose }: WaitlistModalProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const bodyId = useId();

  // Lock body scroll and restore focus to the trigger when the dialog closes.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      previouslyFocused?.focus?.();
    };
  }, [open]);

  // Escape to close; Tab kept inside the dialog so focus can't wander behind it.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      const first = focusable?.[0];
      const last = focusable?.[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
    >
      {/* Backdrop — click to dismiss. */}
      <button
        type="button"
        aria-label={t("waitlist.modal.close")}
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-background/80 backdrop-blur-sm"
      />

      <div
        ref={dialogRef}
        className="liquid-glass animate-fade-up relative w-full max-w-md rounded-2xl bg-card/90 p-6 text-left shadow-editorial md:p-7"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t("waitlist.modal.close")}
          className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        <h2
          id={titleId}
          className="pr-8 font-sans text-xl font-semibold tracking-tight text-foreground"
        >
          {t("waitlist.modal.title")}
        </h2>
        <p id={bodyId} className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {t("waitlist.modal.body")}
        </p>

        <WaitlistForm autoFocus className="mt-6 max-w-none" />
      </div>
    </div>,
    document.body,
  );
}
